import type { PrismaClient } from '@prisma/client';
import { Router } from 'express';
import { z } from 'zod';
import webpush from 'web-push';
import type { Config } from '../../core/config.js';
import { asyncHandler, parse } from '../../core/http.js';
import { authenticate } from '../auth/auth.middleware.js';

/**
 * Web Push (phone notifications for the installed PWA). In-app notifications are the source of truth; this
 * relays the unread ones to each user's subscribed devices. Off unless VAPID keys are configured.
 */
export const pushEnabled = (c: Pick<Config, 'VAPID_PUBLIC_KEY' | 'VAPID_PRIVATE_KEY'>) =>
  !!(c.VAPID_PUBLIC_KEY && c.VAPID_PRIVATE_KEY);

type Sub = { endpoint: string; keys: { p256dh: string; auth: string } };
type Sender = (sub: Sub, payload: string) => Promise<void>;
let senderOverride: Sender | null = null;
export const setPushSenderForTests = (s: Sender | null) => {
  senderOverride = s;
};

export async function pushPendingNotifications(
  db: PrismaClient,
  config: Config,
  now = new Date(),
): Promise<number> {
  if (!pushEnabled(config)) return 0;
  const send: Sender =
    senderOverride ??
    (async (sub, payload) => {
      webpush.setVapidDetails(
        config.VAPID_SUBJECT,
        config.VAPID_PUBLIC_KEY!,
        config.VAPID_PRIVATE_KEY!,
      );
      await webpush.sendNotification(sub, payload, { TTL: 3600 });
    });
  const since = new Date(now.getTime() - 86_400_000);
  const pending = await db.notification.findMany({
    where: { pushedAt: null, readAt: null, createdAt: { gte: since } },
    orderBy: { id: 'asc' },
    take: 100,
  });
  let sent = 0;
  for (const n of pending) {
    const subs = await db.pushSubscription.findMany({ where: { userId: n.userId } });
    for (const s of subs) {
      try {
        await send(
          { endpoint: s.endpoint, keys: { p256dh: s.p256dh, auth: s.auth } },
          JSON.stringify({ title: n.title, body: n.body ?? '', url: n.link ?? '/' }),
        );
        sent += 1;
      } catch (e) {
        const code = (e as { statusCode?: number }).statusCode;
        if (code === 404 || code === 410)
          await db.pushSubscription.delete({ where: { id: s.id } }).catch(() => undefined); // the device is gone
      }
    }
    await db.notification.update({ where: { id: n.id }, data: { pushedAt: now } });
  }
  return sent;
}

export function createPushRouter({ db, config }: { db: PrismaClient; config: Config }) {
  const router = Router();
  router.use('/push', authenticate({ db, config }));
  router.get('/push/public-key', (_req, res) => {
    res.json({
      enabled: pushEnabled(config),
      publicKey: pushEnabled(config) ? config.VAPID_PUBLIC_KEY : null,
    });
  });
  router.post(
    '/push/subscribe',
    asyncHandler(async (req, res) => {
      const b = parse(
        z.object({
          endpoint: z.string().url().max(500),
          keys: z.object({ p256dh: z.string().max(200), auth: z.string().max(100) }),
        }),
        req.body,
      );
      if (!/^https:\/\//.test(b.endpoint))
        return void res
          .status(400)
          .json({ error: { code: 'VALIDATION_ERROR', message: 'Push endpoints must use https' } });
      await db.pushSubscription.upsert({
        where: { userId_endpoint: { userId: req.auth!.userId, endpoint: b.endpoint } },
        create: {
          companyId: req.auth!.companyId,
          userId: req.auth!.userId,
          endpoint: b.endpoint,
          p256dh: b.keys.p256dh,
          auth: b.keys.auth,
          userAgent: String(req.headers['user-agent'] ?? '').slice(0, 200),
        },
        update: { p256dh: b.keys.p256dh, auth: b.keys.auth },
      });
      res.status(201).json({ ok: true });
    }),
  );
  router.post(
    '/push/unsubscribe',
    asyncHandler(async (req, res) => {
      const b = parse(z.object({ endpoint: z.string().max(500) }), req.body);
      await db.pushSubscription.deleteMany({
        where: { userId: req.auth!.userId, endpoint: b.endpoint },
      });
      res.status(204).end();
    }),
  );
  return router;
}
